import { faEllipsisV, faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  Stack
} from '@mui/material';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import type { AxiosError } from 'axios';
import { useAtom } from 'jotai';
import {
  bindMenu,
  bindTrigger,
  usePopupState
} from 'material-ui-popup-state/hooks';
import { useRef } from 'react';

import KeyValue, { type KeyValueModel } from '@/components/KeyValue';
import PERMISSIONS from '@/constants/auth.constants';
import useConfirm from '@/hooks/useConfirm';
import useHandleError from '@/hooks/useHandleError';
import Panel from '@/layout/Panel';
import {
  currentRequestAtom,
  requestDetailsExpandedAtom,
  useDeleteRequest
} from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.store';
import type { RequestDTO } from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.types';
import getStatusColor from '@/pages/Administration/AdministrationPcapRequests/AdministrationPcapRequests.utils';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import queryClient from '@/services/BaseApi.service';
import checkPermissions from '@/services/permissions';
import userAtom from '@/store/user.store';
import type { BaseDownloadDTO } from '@/types/common';
import { humanizeFileSize } from '@/utils/Common.utils';
import getDateTime from '@/utils/getDateTime.utils';

const getRequestDetails = (request: RequestDTO) => {
  const requestDetails: KeyValueModel[] = [
    {
      label: 'Sensors',
      value: request?.details?.sensors.join(', ')
    },
    {
      label: 'Request Start',
      value: getDateTime(request?.details?.start, true)
    },
    {
      label: 'Request End',
      value: getDateTime(request?.details?.end, true)
    },
    {
      label: 'Stenographer Query',
      value: request?.details?.query
    },
    {
      label: 'Purpose',
      value: request?.details?.purpose
    }
  ];
  return requestDetails;
};

const PcapRequestDetails = () => {
  const [, setRequestDetailsExpanded] = useAtom(requestDetailsExpandedAtom);
  const [currentUser] = useAtom(userAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [currentRequest] = useAtom(currentRequestAtom);
  const handleError = useHandleError();
  const deleteMutation = useDeleteRequest();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'moreMenu'
  });
  const openMenuButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleDownload = async (filename: string, requestId: number) => {
    try {
      const data: BaseDownloadDTO = await queryClient.fetchQuery({
        queryKey: [`pcaps/${requestId}/${filename}`]
      });
      const link = data.download_url;
      if (link) {
        window.open(link, '_blank');
      }
    } catch (error) {
      handleError(error as AxiosError, 'Failed to download pcap file');
    }
  };

  const { ConfirmationModal: PcapRequestDeleteModal, confirm: confirmDelete } =
    useConfirm({
      title: 'Delete Pcap Request',
      message: 'Please confirm that you want to delete this pcap request.',
      button: 'Delete'
    });

  const handleDelete = async () => {
    const confirmed = await confirmDelete();
    if (confirmed && currentRequest) {
      deleteMutation.mutate(currentRequest?.id);
    }
    popupState.close();
  };

  return (
    <Panel
      action={
        checkPermissions(
          PERMISSIONS.PCAPS_DELETE,
          currentUser,
          selectedOrganization
        ) && (
          <>
            <Button
              size="medium"
              variant="outlined"
              {...bindTrigger(popupState)}
              ref={openMenuButtonRef}
            >
              <FontAwesomeIcon icon={faEllipsisV} size="lg" />
            </Button>
            <Menu
              {...bindMenu(popupState)}
              anchorEl={openMenuButtonRef.current}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem key="Delete" onClick={handleDelete}>
                <ListItemIcon sx={{ color: 'error.main' }}>
                  <FontAwesomeIcon icon={faTrashCan} size="lg" />
                </ListItemIcon>
                <ListItemText sx={{ color: 'error.main' }}>
                  <Typography>Delete Request</Typography>
                </ListItemText>
              </MenuItem>
            </Menu>
            <PcapRequestDeleteModal />
          </>
        )
      }
      title={
        <>
          <Typography
            variant="h6"
            component="span"
            sx={{ mt: 1, mb: 0.5, mr: 1 }}
          >
            Details
          </Typography>
          {currentRequest?.status && (
            <Chip
              variant="outlined"
              label={currentRequest?.status}
              color={getStatusColor(currentRequest?.status)}
            />
          )}
        </>
      }
      onClose={() => setRequestDetailsExpanded(false)}
    >
      <Stack
        spacing={1}
        direction="column"
        divider={<Divider orientation="horizontal" />}
      >
        {currentRequest && (
          <KeyValue items={getRequestDetails(currentRequest)} />
        )}
        {currentRequest?.pcaps && currentRequest.pcaps?.length > 0 && (
          <div>
            <Typography variant="h6" sx={{ mb: 1 }}>
              PCAP Files
            </Typography>
            <Stack direction="column" spacing={1}>
              {currentRequest?.pcaps?.map((pcap) => (
                <Typography key={pcap.filename}>
                  <Button
                    onClick={() =>
                      handleDownload(pcap.filename, currentRequest.id)
                    }
                  >
                    {pcap.filename}
                  </Button>
                  <span> ({humanizeFileSize(pcap.filesize || 0)})</span>
                </Typography>
              ))}
            </Stack>
          </div>
        )}
      </Stack>
    </Panel>
  );
};

export default PcapRequestDetails;
